/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC } from "react";
import { SimpleAlert } from "./SimpleAlert";
import { useTranslation } from "react-i18next";
import { ErrorAlertPropTypes } from "../../types/components/alert";
import { NestedErrorMessage } from "../../types/components/errorMessage";
import {
  isNetworkErrorMessage,
  isNetworkErrorPayload,
} from "../../utils/networkError";

interface SimpleError {
  message: string;
  params?: Record<string, string>;
  translate?: boolean;
}

interface ValidatorError {
  field: string;
  value: unknown;
  message: string;
}

interface HttpErrorResponse {
  response: {
    data: {
      message: string;
      errors?: ValidatorError[];
    };
  };
}

export const ErrorAlert: FC<ErrorAlertPropTypes> = (
  props: ErrorAlertPropTypes
): React.JSX.Element => {
  let message = "";
  const { error, title, variant, children, showError = true } = props;
  const { t } = useTranslation();
  if (typeof error === "string") {
    message = error as string;
  } else if (error && typeof error === "object") {
    const httpError = error as HttpErrorResponse;
    if (httpError.response) {
      const { response } = httpError;
      if (response.data) {
        const { data } = response;
        if (data.message) {
          message = data.message;
        } else if (data.errors && data.errors.length > 0) {
          message = data.errors[0].field + ":" + data.errors[0].message;
        } else {
          // eslint-disable-next-line no-console
          console.log("unknown error response", response);
        }
      }
    } else if ((error as NestedErrorMessage)?.errors?.length) {
      const invalidError = error as NestedErrorMessage;
      const firstError = invalidError?.errors?.[0];
      message = firstError
        ? `${firstError.field ? firstError.field + ": " : ""}${
            firstError.message
          }`
        : "";
    } else if ((error as SimpleError)?.message) {
      message = (error as SimpleError)?.message;
    } else {
      message = t("error_messages.server_error");
      // eslint-disable-next-line no-console
      console.log("Unparser error", error);
    }
    if ((error as SimpleError)?.params) {
      const params = (error as SimpleError).params ?? {};
      Object.keys(params).forEach((key) => {
        if (message.includes(`{${key}}`)) {
          message = message.replace(`{${key}}`, params[key]);
        } else if ((error as SimpleError)?.translate) {
          message = `${t(message)}`.replace(`{${key}}`, params[key]);
        } else {
          // @ts-ignore
          if (error?.type === "UnprocessableEntityException") {
            message = `${t(params[key])} ${message?.toLowerCase()} `;
          } else {
            message = `${message}\n${t(key)}: ${params[key]}`;
          }
        }
      });
    }
  } else {
    message = t("error_messages.server_error");
  }
  // Connection errors are shown by NetworkRequestErrorAlert / NetworkStatusAlert,
  // never by error alerts.
  if (
    isNetworkErrorPayload(error) ||
    isNetworkErrorMessage(message, t("common:server_error_code.502"))
  )
    return <></>;
  return (
    <>
      <SimpleAlert
        dismissible
        {...props}
        showError={showError}
        // @ts-ignore
        message={message}
        title={title}
        variant={variant || "danger"}
        children={children}
      />
    </>
  );
};
